import { buildCommunityPostCommentPath, notificationCommunityPath } from './community-path';

/**
 * Where an "engaged with your work" row actually happened.
 *
 * The section names an act — "upvoted your post" — and a member's next question is
 * always "which post?". Naming a thing and then not linking to it is the one thing
 * this section can get wrong, so every row that CAN resolve a destination does.
 *
 * Pure on purpose, with the slug lookups injected: which keys a row carries differs by
 * type (a vote batch stores its slugs in metadata, a reaction on artwork stores only an
 * image id), and that per-type difference is exactly the kind of thing that rots into a
 * dead link when it is re-derived at three call sites. Resolved once, proven next door
 * in `shared/test/network-engagement-target.spec.ts`.
 *
 * Returns a town-square-relative path, matching `notificationCommunityPath` — the
 * caller joins it to an origin.
 */

export interface EngagementTargetRow {
  targetType: string | null;
  targetId: string | null;
  metadata?: Record<string, any> | null;
}

export interface EngagementTargetSlugs {
  /** The viewer's own profile slug — they own every artwork this section points at. */
  profileSlug?: string | null;
  /** Image id -> image slug, for artwork and artwork-comment targets. */
  imageSlugById?: ReadonlyMap<string, string>;
  /** Image-comment id -> the id of the image it sits on. */
  imageIdByCommentId?: ReadonlyMap<string, string>;
  /** Community-comment id -> `{ communitySlug, postSlug }` for the post it sits under. */
  communityCommentLocation?: ReadonlyMap<string, { communitySlug: string; postSlug: string }>;
  /**
   * Community-post id -> `{ communitySlug, postSlug }` for rows whose metadata has
   * neither slug. Vote batches store them; reactions do not.
   */
  communityPostLocation?: ReadonlyMap<string, { communitySlug: string; postSlug: string }>;
}

/** Callers spell the same target four ways across writers; normalise once. */
export function engagementTargetKind(targetType: string | null): string {
  return (targetType ?? '').toLowerCase().replace(/_/g, '');
}

function imagePath(profileSlug: string, imageSlug: string, commentId?: string): string {
  const base = `/${profileSlug}/image/${imageSlug}`;
  return commentId ? `${base}?c=${encodeURIComponent(commentId)}` : base;
}

function readSlug(metadata: Record<string, any> | null | undefined, key: string): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Slugs already sitting on the row, or null when either one is missing. */
function locationFromMetadata(
  metadata: Record<string, any> | null | undefined
): { communitySlug: string; postSlug: string } | null {
  const communitySlug = readSlug(metadata, 'communitySlug');
  const postSlug = readSlug(metadata, 'postSlug') ?? readSlug(metadata, 'communityPostSlug');
  if (!communitySlug || !postSlug) return null;
  return { communitySlug, postSlug };
}

function locationOrNull(
  location: { communitySlug: string; postSlug: string } | undefined
): { communitySlug: string; postSlug: string } | null {
  if (!location) return null;
  const communitySlug = location.communitySlug.trim();
  const postSlug = location.postSlug.trim();
  if (!communitySlug || !postSlug) return null;
  return { communitySlug, postSlug };
}

export function engagementTargetPath(row: EngagementTargetRow, slugs: EngagementTargetSlugs): string | null {
  const targetId = row.targetId == null ? '' : String(row.targetId).trim();
  if (targetId === '') return null;

  const kind = engagementTargetKind(row.targetType);
  const profileSlug = slugs.profileSlug?.trim() || null;

  if (kind === 'image') {
    const imageSlug = slugs.imageSlugById?.get(targetId);
    if (!profileSlug || !imageSlug) return null;
    return imagePath(profileSlug, imageSlug);
  }

  if (kind === 'imagecomment') {
    const imageId = slugs.imageIdByCommentId?.get(targetId);
    const imageSlug = imageId ? slugs.imageSlugById?.get(imageId) : undefined;
    // Land ON the comment. A reader sent to the top of their own artwork page has to
    // hunt for the thing the row just told them about.
    if (!profileSlug || !imageSlug) return null;
    return imagePath(profileSlug, imageSlug, targetId);
  }

  if (kind === 'communitycomment') {
    // The map is a lookup (a reaction stores no slugs). Comment upvotes already joined
    // the same pair onto the row, and that metadata is the link.
    const location =
      locationOrNull(slugs.communityCommentLocation?.get(targetId)) ?? locationFromMetadata(row.metadata);
    if (!location) return null;
    return buildCommunityPostCommentPath(location.communitySlug, location.postSlug, targetId);
  }

  if (kind === 'communitypost') {
    // Vote batches store both slugs, the same pair the notification email reads.
    // A reaction stores only the actor and the emoji, so the lookup map is the fallback.
    const fromMetadata = notificationCommunityPath(row);
    if (fromMetadata) return fromMetadata;
    const location = locationOrNull(slugs.communityPostLocation?.get(targetId));
    if (!location) return null;
    return notificationCommunityPath({
      metadata: { communitySlug: location.communitySlug, postSlug: location.postSlug },
    });
  }

  return null;
}

/** The noun a target is — artwork, post or comment — or null when it is none of them. */
export function engagementTargetNoun(targetType: string | null): 'artwork' | 'post' | 'comment' | null {
  const kind = engagementTargetKind(targetType);
  if (kind === 'image') return 'artwork';
  if (kind === 'communitypost') return 'post';
  if (kind.endsWith('comment')) return 'comment';
  return null;
}

/**
 * What to call the thing someone engaged with.
 *
 * Keyed on the TARGET, not the notification type: `COMMUNITY_REACTION` fires on
 * artwork, on artwork comments and on community posts, so a per-type string said
 * "reacted to your post" over a link to a painting. Naming the wrong noun is worse
 * than naming none — it tells a member their post got attention when it was their work.
 *
 * Returns the leaf of an i18n key under `network.suggested-connections.`.
 */
export function engagementReasonKey(type: string, targetType: string | null): string {
  const noun = engagementTargetNoun(targetType);
  if (!noun) return 'engaged-generic';

  const verb = VERB_BY_TYPE[type.toUpperCase()];
  if (!verb) return 'engaged-generic';

  return `engaged-${verb}-${noun}`;
}

const VERB_BY_TYPE: Record<string, string> = {
  LIKE: 'liked',
  COMMUNITY_REACTION: 'reacted',
  COMMUNITY_VOTE_BATCH: 'upvoted',
  COMMUNITY_VOTE: 'upvoted',
  COMMENT_UPVOTE: 'upvoted',
};
